import { useEffect } from "react"

import { applyLanguageIfDifferent, isSupportedLanguage, useLanguagePreference } from "@/entities/user/language"
import { useGetUserSettingsQuery } from "@/entities/user/settings"

import { WebApp } from "@/shared/telegram"
import type { LanguageCode } from "@/shared/types/language.types"

/**
 * Applies the resolved interface language to i18n whenever its sources change.
 * Priority: CloudStorage override → user settings → Telegram user language → 'en'.
 *
 * A lower-priority source is only consulted once every higher-priority one has settled, so a
 * cold start never paints the Telegram language and then flips to the saved one. The Telegram
 * fallback is applied without persisting.
 *
 * `provisionalLanguage` (an ad visitor's Telegram language) is shown, unpersisted, while those sources settle, so
 * the first paint is not the detector's guess from the device language.
 */
export const useLanguageController = (provisionalLanguage: LanguageCode | null = null): void => {
  const { defaultLanguage, hasSettledSettings } = useGetUserSettingsQuery(undefined, {
    selectFromResult: ({ data, isError, isSuccess }) => ({
      defaultLanguage: data?.default_language,
      hasSettledSettings: isSuccess || isError
    })
  })
  const { language: personalLang, isReady: hasSettledPreference } = useLanguagePreference()

  useEffect(() => {
    if (isSupportedLanguage(personalLang)) {
      applyLanguageIfDifferent(personalLang)
      return
    }
    if (!hasSettledPreference || !hasSettledSettings) {
      if (provisionalLanguage) applyLanguageIfDifferent(provisionalLanguage, { persist: false })
      return
    }
    if (isSupportedLanguage(defaultLanguage)) {
      applyLanguageIfDifferent(defaultLanguage)
      return
    }
    const telegramLang = WebApp.initDataUnsafe.user?.language_code
    applyLanguageIfDifferent(isSupportedLanguage(telegramLang) ? telegramLang : "en", { persist: false })
  }, [personalLang, hasSettledPreference, defaultLanguage, hasSettledSettings, provisionalLanguage])
}
